import { GeoPackageAPI, GeoPackage, setSqljsWasmLocateFile } from '@ngageoint/geopackage';
import type { Feature, FeatureCollection } from 'geojson';

/**
 * Error thrown when a GeoPackage file cannot be parsed or is invalid.
 */
export class GeoPackageConversionError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'GeoPackageConversionError';
  }
}

/**
 * Configure the sql.js WASM file location for browser environments.
 * This must be called before any GeoPackage operations if using a custom WASM path.
 */
export function configureWasmPath(locateFile: (filename: string) => string): void {
  setSqljsWasmLocateFile(locateFile);
}

/**
 * Converts a GeoPackage file (as ArrayBuffer) to a GeoJSON FeatureCollection.
 *
 * Opens the GeoPackage, reads all feature tables, and combines their features
 * into a single GeoJSON FeatureCollection. Feature properties are preserved,
 * and a `_sourceTable` property is added to identify which table each feature
 * came from.
 *
 * @param data - ArrayBuffer containing the GeoPackage file bytes
 * @returns Promise resolving to a GeoJSON FeatureCollection
 * @throws GeoPackageConversionError if the file cannot be opened or parsed
 *
 * Validates: Requirements 9.3
 */
export async function toGeoJSON(data: ArrayBuffer): Promise<FeatureCollection> {
  if (!data || data.byteLength === 0) {
    throw new GeoPackageConversionError('Cannot convert empty GeoPackage data');
  }

  let geoPackage: GeoPackage | null = null;

  try {
    const uint8Array = new Uint8Array(data);
    geoPackage = await GeoPackageAPI.open(uint8Array);
  } catch (error) {
    throw new GeoPackageConversionError(
      'Failed to open GeoPackage file. The file may be corrupted or not a valid GeoPackage.',
      error
    );
  }

  try {
    const featureTables = geoPackage.getFeatureTables();

    if (featureTables.length === 0) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    const allFeatures: Feature[] = [];

    for (const tableName of featureTables) {
      const features = extractFeaturesFromTable(geoPackage, tableName);
      allFeatures.push(...features);
    }

    return {
      type: 'FeatureCollection',
      features: allFeatures,
    };
  } catch (error) {
    if (error instanceof GeoPackageConversionError) {
      throw error;
    }
    throw new GeoPackageConversionError('Failed to extract features from GeoPackage.', error);
  } finally {
    geoPackage.close();
  }
}

/**
 * Extracts GeoJSON features from a single feature table in the GeoPackage.
 *
 * @param geoPackage - The open GeoPackage instance
 * @param tableName - Name of the feature table to read
 * @returns Array of GeoJSON Features with preserved properties
 */
function extractFeaturesFromTable(geoPackage: GeoPackage, tableName: string): Feature[] {
  const features: Feature[] = [];

  try {
    const iterator = geoPackage.iterateGeoJSONFeatures(tableName);

    for (const feature of iterator) {
      // Ensure the feature has valid geometry
      if (feature && feature.geometry) {
        const outputFeature: Feature = {
          type: 'Feature',
          geometry: feature.geometry,
          properties: {
            ...feature.properties,
            _sourceTable: tableName,
          },
        };

        // Preserve the feature ID if present
        if (feature.id !== undefined) {
          outputFeature.id = feature.id;
        }

        features.push(outputFeature);
      }
    }
  } catch (error) {
    throw new GeoPackageConversionError(
      `Failed to read features from table "${tableName}".`,
      error
    );
  }

  return features;
}

/**
 * Gets the list of feature table names from a GeoPackage file.
 * Useful for inspecting the contents before full conversion.
 *
 * @param data - ArrayBuffer containing the GeoPackage file bytes
 * @returns Promise resolving to an array of table names
 * @throws GeoPackageConversionError if the file cannot be opened
 */
export async function getFeatureTableNames(data: ArrayBuffer): Promise<string[]> {
  if (!data || data.byteLength === 0) {
    throw new GeoPackageConversionError('Cannot read empty GeoPackage data');
  }

  let geoPackage: GeoPackage | null = null;

  try {
    const uint8Array = new Uint8Array(data);
    geoPackage = await GeoPackageAPI.open(uint8Array);
    return geoPackage.getFeatureTables();
  } catch (error) {
    if (error instanceof GeoPackageConversionError) {
      throw error;
    }
    throw new GeoPackageConversionError(
      'Failed to open GeoPackage file. The file may be corrupted or not a valid GeoPackage.',
      error
    );
  } finally {
    if (geoPackage) {
      geoPackage.close();
    }
  }
}
